import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input from './Input';
import { SearchIcon } from './Icons';
import CloseIcon from './CloseIcon';

/**
 * SearchInputV2 - A search input component with debounce functionality.
 *
 * @param {string} placeholder - Placeholder text for the input.
 * @param {string} value - Controlled value for the input.
 * @param {function} onChange - Callback to update the value in the parent component.
 * @param {number} debounceTime - Debounce time in milliseconds to delay the onChange call.
 *
 * @returns {JSX.Element} - Rendered search input with optional clear button.
 */
const SearchInputV2 = ({ placeholder = 'Search', value, onChange, debounceTime = 300 }) => {
  const [searchTerm, setSearchTerm] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      onChange(searchTerm);
    }, debounceTime);

    return () => clearTimeout(handler);
  }, [searchTerm, debounceTime, onChange]);

  const onClickClearSearch = () => {
    setSearchTerm('');
    onChange('');
  };

  return (
    <div className="relative w-full sm:max-w-lg lg:max-w-xl" role="search">
      <Input
        variant="tertiary"
        type="text"
        id="search"
        customClass="pl-[53px] pr-[40px] bg-white rounded-[10px] h-[46px]"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={placeholder}
        aria-label="Search Input"
        aria-live="polite"
      />
      <div className="absolute bottom-3 left-4">
        <SearchIcon />
      </div>
      {searchTerm && (
        <button
          className="absolute bottom-4 right-3"
          onClick={onClickClearSearch}
          aria-label="Clear search"
        >
          <CloseIcon size="12" className="!text-gray-400" />
        </button>
      )}
    </div>
  );
};

SearchInputV2.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  debounceTime: PropTypes.number,
};

export default SearchInputV2;
